import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HelpPage = () => {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [openFaq, setOpenFaq] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqCategories = {
    'getting-started': {
      title: '1. Getting Started',
      icon: '🚀',
      faqs: [
        {
          question: 'Q1. What is SpreadexAsia?',
          answer: 'SpreadexAsia is an AI-powered auto trading platform that trades in Forex and crypto markets for you—smart, fast, and effortless.'
        },
        {
          question: 'Q2. How does SpreadexAsia work?',
          answer: 'Our AI analyzes market trends and executes trades automatically, helping you earn profits without lifting a finger.'
        },
        {
          question: 'Q3. How do I start trading?',
          answer: 'Create an account, deposit funds, and activate the AI—your trading journey begins instantly.'
        },
        {
          question: 'Q4. Do I need trading experience?',
          answer: 'No experience needed. SpreadexAsia handles everything, making trading easy for beginners and pros alike.'
        },
        {
          question: 'Q5. How quickly can I start earning after activating SpreadexAsia?',
          answer: 'Once your account is set up and funds are deposited, the AI starts trading immediately, giving you potential earnings from day one.'
        }
      ]
    },
    'trading-earnings': {
      title: '2. Trading & Earnings',
      icon: '💰',
      faqs: [
        {
          question: 'Q6. How do traders earn money with SpreadexAsia?',
          answer: 'The AI identifies profitable trades and acts instantly, turning market opportunities into potential daily earnings.'
        },
        {
          question: 'Q7. How much can I earn daily?',
          answer: 'Returns vary by investment and market conditions. Many traders see 5%–25% growth daily.'
        },
        {
          question: 'Q8. Can SpreadexAsia work while I sleep or am away?',
          answer: 'Yes. The AI trades 24/7, so you can earn even while you sleep or focus on other things.'
        },
        {
          question: 'Q9. Do I need to monitor the market constantly?',
          answer: 'No. SpreadexAsia automatically tracks market movements and executes trades, so you don\'t have to watch the market all the time.'
        },
        {
          question: 'Q10. Can I trade multiple markets at once?',
          answer: 'Yes. SpreadexAsia can operate in multiple markets simultaneously, based on your preferences.'
        }
      ]
    },
    'account-investment': {
      title: '3. Account & Investment',
      icon: '📈',
      faqs: [
        {
          question: 'Q11. Is there a minimum or maximum investment?',
          answer: 'You choose your investment amount freely, according to your goals and risk appetite.'
        },
        {
          question: 'Q12. How do I deposit funds?',
          answer: 'We support secure options like bank transfer, UPI, and crypto deposits.'
        },
        {
          question: 'Q13. How do I withdraw profits?',
          answer: 'Withdrawals are fast and safe—your earnings are always in your control.'
        },
        {
          question: 'Q14. Does SpreadexAsia charge per trade?',
          answer: 'No. Only the subscription or software package fees apply—no extra trade charges.'
        },
        {
          question: 'Q15. Are my personal and financial details safe?',
          answer: 'Yes. We follow strict security and privacy standards to keep your information protected.'
        }
      ]
    },
    'platform-features': {
      title: '4. Platform & Features',
      icon: '🔐',
      faqs: [
        {
          question: 'Q16. Can I use SpreadexAsia on mobile and desktop?',
          answer: 'Yes. Fully compatible on mobile and browser for trading anytime, anywhere.'
        },
        {
          question: 'Q17. How is SpreadexAsia different from other trading software?',
          answer: 'It combines cutting-edge AI, real-time analysis, and full automation—no other platform trades this smart.'
        },
        {
          question: 'Q18. Can I track my earnings in real-time?',
          answer: 'Yes. Monitor trades, profits, and performance instantly through your dashboard.'
        },
        {
          question: 'Q19. How often does SpreadexAsia execute trades?',
          answer: 'Our AI acts whenever the market presents a profitable opportunity.'
        },
        {
          question: 'Q20. Do I need technical knowledge to set it up?',
          answer: 'No. The platform is beginner-friendly, with simple setup and easy-to-follow instructions.'
        }
      ]
    },
    'customization-control': {
      title: '5. Customization & Control',
      icon: '⚙️',
      faqs: [
        {
          question: 'Q21. Can I control how the auto trading software operates?',
          answer: 'Absolutely. You can adjust trade size, risk level, and market preferences to fit your goals.'
        },
        {
          question: 'Q22. Can I influence the trading strategy?',
          answer: 'Yes. Customize key parameters such as risk tolerance, trade frequency, and investment allocation to suit your style.'
        },
        {
          question: 'Q23. How often should I check my account?',
          answer: 'While the AI trades automatically, checking regularly helps track earnings and manage risk smartly.'
        }
      ]
    },
    'support-security': {
      title: '6. Support & Security',
      icon: '💻',
      faqs: [
        {
          question: 'Q24. What support is available?',
          answer: 'Dedicated support is ready to guide you through setup, trading, and any questions.'
        },
        {
          question: 'Q25. Is SpreadexAsia safe and secure?',
          answer: 'Yes. Advanced encryption and strict protocols protect both your funds and personal data.'
        },
        {
          question: 'Q26. Are there any hidden fees?',
          answer: 'No hidden charges—transparent pricing with only the subscription or package you choose.'
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Hero Section */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in-down">
              How Can We <span className="text-yellow-500">Help You?</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto animate-fade-in-up animate-delay-200">
              Find answers to common questions or contact our 24/7 support team
            </p>
          </div>

          {/* FAQ Section */}
          <section className="animate-fade-in-up animate-delay-300">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 animate-scale-in">
              Frequently Asked Questions
            </h2>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {Object.entries(faqCategories).map(([key, category], index) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover-lift ${
                    activeCategory === key
                      ? 'bg-yellow-500 text-black animate-glow'
                      : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.title}
                </button>
              ))}
            </div>

            {/* FAQ Content */}
            <div className="max-w-4xl mx-auto space-y-4">
              {faqCategories[activeCategory].faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-black rounded-xl border border-gray-800 hover:border-yellow-500 transition-all duration-500 overflow-hidden hover-lift animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-900/50 transition-all duration-300"
                  >
                    <h3 className="text-lg md:text-xl font-bold text-white pr-4">
                      {faq.question}
                    </h3>
                    <span className="text-2xl text-yellow-500 flex-shrink-0 transition-transform duration-500" style={{ transform: openFaq === index ? 'rotate(45deg)' : 'rotate(0deg)' }}>
                      +
                    </span>
                  </button>
                  <div
                    className={`transition-all duration-500 ease-in-out ${
                      openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                    style={{ overflow: 'hidden' }}
                  >
                    <p className="px-6 pb-6 text-gray-300 leading-relaxed animate-fade-in">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>


        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HelpPage;
